import LinkButton from 'components/controls/links/link-button';
import MarkdownParser from 'components/rich-text/markdown-parser';
import styles from './minimal-header.module.scss';
import ChevronR from 'assets/icons/chevron-r.svg';
import { getUrlString, type ILink } from 'helpers/helpers';
import LanguageFilter from 'components/controls/language-filter';
import { type ReactNode } from 'react';
import {
	getActionAttributes,
	getContextAttributes,
	type AnalyticsContext,
} from 'lib/analytics/analyticsAttributes';

const MinimalHeader = ({
	heading,
	description,
	links,
	children,
	displayLanguageFilter = false,
	availableLanguages = [],
	languageDescriptions = [],
	hideBottomBorder = false,
	analyticsContext,
}: IProps) => {
	return (
		<header
			className={styles.header}
			{...getContextAttributes(analyticsContext)}
		>
			<div
				className={`${styles.content} ${!hideBottomBorder ? styles.contentBorder : ''}`}
			>
				<div className={styles.headingContainer}>
					<h1 className={styles.heading}>
						<MarkdownParser>{heading}</MarkdownParser>
					</h1>
					{displayLanguageFilter && (
						<div className={styles.languageFilterContainer}>
							<LanguageFilter
								availableLanguages={availableLanguages}
								descriptions={languageDescriptions}
							/>
						</div>
					)}
				</div>
				{description && (
					<h2 className={styles.description}>
						<MarkdownParser>{description}</MarkdownParser>
					</h2>
				)}
				{children}
				{!!links?.length && (
					<ul className={styles.linkList}>
						{links?.map((link) => (
							<li className={styles.linkItem} key={link.cta || link.text}>
								<LinkButton
									rightIcon={<ChevronR />}
									text={link.text || link.cta}
									href={getUrlString(link)}
									{...getActionAttributes({
										contentName: link.text || link.cta,
									})}
								/>
							</li>
						))}
					</ul>
				)}
			</div>
		</header>
	);
};

interface IProps {
	children?: ReactNode;
	description?: string; // cannot be portable text (array of blocks)
	heading: string;
	links?: ILink[];
	analyticsContext?: AnalyticsContext;
	displayLanguageFilter?: boolean;
	availableLanguages?: { language: string }[];
	languageDescriptions?: {
		id: string;
		description: string;
	}[];
	hideBottomBorder?: boolean;
}

export default MinimalHeader;
